import { Component, DoCheck, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ScheduledEvent, WaitlistEntryStatus } from '../../../../../../../../../../../models/scheduled-event';
import { Customer, CustomerSearchResult } from '../../../../../../../../../../../models/customer';
import { Event } from '../../../../../../../../../../../models/event';
import { CallbackResult, CallbackResultStatus } from '../../../../../../../../../../../utils';
import {
  blankInviteExpiry,
  InviteExpiry,
  inviteExpiryRequestFields,
  inviteExpiryValidationError
} from '../../../../../../../../../../../models/invite-expiry';
import { ScheduledEventService } from '../../../../../../../../../../../services/scheduled-event/scheduled-event.service';
import { EventService } from '../../../../../../../../../../../services/event/event.service';
import { EventTypeService } from '../../../../../../../../../../../services/event-type/event-type.service';
import { EventType } from '../../../../../../../../../../../models/event-type';
import { AgeRestrictionBypass, eventAgeAsOfDate } from '../../../../../../../../../../../models/age-restriction';
import { SelectCustomerModalComponent } from '../../../../../../../../modals/select-customer-modal/select-customer-modal.component';
import {
  CreateNewCustomerModalComponent
} from '../../../../../../../../customers/modals/create-new-customer-modal/create-new-customer-modal.component';


@Component({
  selector: 'app-invite-to-event-modal',
  templateUrl: './invite-to-event-modal.component.html',
  styleUrls: ['./invite-to-event-modal.component.css']
})
export class InviteToEventModalComponent implements OnInit, DoCheck {
  @Input() scheduledEvent: ScheduledEvent;
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public selectedCustomerId: number | null = null;
  public selectedCustomerName = '';
  public selectedCustomerBirthday: string | null = null;

  public inviteNote = '';
  public expiry: InviteExpiry = blankInviteExpiry();
  public sendInviteToParentsAndGuardians = false;

  public errorMessage = '';
  public serverError: number = null;

  public loadingEvent = false;
  public loadingEventType = false;
  public loadingSave = false;

  public event: Event = null;
  public eventType: EventType = null;

  public readonly ageRestriction = new AgeRestrictionBypass();

  constructor(
    public activeModal: NgbActiveModal,
    private scheduledEventService: ScheduledEventService,
    private eventService: EventService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.loadEvent();
    this.loadEventType();
  }

  ngDoCheck() {
    if ((this.event === null) || (this.eventType === null)) {
      this.ageRestriction.refresh('', [], null);
      return;
    }

    const participants = (this.selectedCustomerId === null) ? [] : [{
      key: this.selectedCustomerId.toString(),
      name: this.selectedCustomerName,
      birthday: this.selectedCustomerBirthday,
    }];

    this.ageRestriction.refresh(
      'event-' + this.event.id.toString(),
      participants,
      this.eventType,
      eventAgeAsOfDate(this.event)
    );
  }

  public openSelectCustomerModal() {
    const modalRef = this.modalService.open(SelectCustomerModalComponent);
    modalRef.componentInstance.excludeCustomerIds = this.getExcludedCustomerIds();
    modalRef.componentInstance.submitCallback = (result: CustomerSearchResult) => {
      this.setSelectedCustomer(result.id, result.first_name, result.last_name, result.birthday);
    };
  }

  public openCreateCustomerModal() {
    const modalRef = this.modalService.open(CreateNewCustomerModalComponent);
    modalRef.componentInstance.savedCallback = (customer: Customer) => {
      this.setSelectedCustomer(customer.id, customer.first_name, customer.last_name, customer.birthday);
    };
  }

  public clearSelectedCustomer() {
    this.selectedCustomerId = null;
    this.selectedCustomerName = '';
    this.selectedCustomerBirthday = null;
  }

  public sendInviteAllowed(): boolean {
    return (this.selectedCustomerId !== null) && !this.loadingSave && !this.loadingEvent &&
      !this.loadingEventType && !this.ageRestriction.blocksSend() &&
      (inviteExpiryValidationError(this.expiry, this.expiryTimeZone()) === null);
  }

  public expiryTimeZone(): string | null {
    return this.event ? this.event.time_zone : null;
  }

  public handleSendInvite() {
    this.errorMessage = '';
    this.loadingSave = true;

    const expiryFields = inviteExpiryRequestFields(this.expiry);

    this.scheduledEventService
      .inviteCustomerToBookEvent(
        this.scheduledEvent.event_id,
        this.selectedCustomerId,
        expiryFields.hoursToAccept,
        this.inviteNote,
        this.sendInviteToParentsAndGuardians,
        expiryFields.newDeadlineDate,
        expiryFields.newDeadlineTime,
        this.ageRestriction.codesFor(this.selectedCustomerId.toString())
      )
      .then(() => {
        this.savedCallback({
          text: this.selectedCustomerName + ' was invited successfully.',
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to perform this action.';
        } else if (reason.status === 400) {
          // The server says exactly why it refused — including, when the
          // customer already has a booking on this event, whether that booking
          // is paid in full or holding a deposit, which decide opposite next
          // steps (PRO-1457). Guessing "already been invited" here hid all of
          // that.
          this.errorMessage = (typeof reason.error === 'string')
            ? reason.error
            : 'The invite could not be sent, most likely because the ' +
              'customer has already been invited to the event.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSave = false; });
  }

  private setSelectedCustomer(customerId: number, firstName: string, lastName: string, birthday: string | null) {
    this.selectedCustomerId = customerId;
    this.selectedCustomerName = firstName + ' ' + lastName;
    this.selectedCustomerBirthday = birthday;
  }

  private getExcludedCustomerIds(): number[] {
    const excludedIds = new Set<number>();

    for (const reservation of this.scheduledEvent.reservations) {
      if (reservation.is_cancelled) { continue; }
      if (reservation.customer_id) {
        excludedIds.add(reservation.customer_id);
      }
    }

    for (const entry of this.scheduledEvent.waitlist_entries) {
      // Exclude only customers with active waitlist entries (waiting, pending,
      // or accepted). Expired or rejected entries don't block re-inviting.
      const isActive = (
        (entry.status === WaitlistEntryStatus.waiting) ||
        (entry.status === WaitlistEntryStatus.pending) ||
        (entry.status === WaitlistEntryStatus.accepted)
      );
      if (isActive && entry.participant_customer_id) {
        excludedIds.add(entry.participant_customer_id);
      }
    }

    return Array.from(excludedIds);
  }

  private loadEvent() {
    this.loadingEvent = true;

    this.eventService
      .getEvent(this.scheduledEvent.event_id)
      .then(event => { this.event = event; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEvent = false; });
  }

  private loadEventType() {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(this.scheduledEvent.event_type_id)
      .then(eventType => { this.eventType = eventType; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEventType = false; });
  }
}
